"use client";

// These files are the single source of truth for BOTH distribution channels
// of the Magnus chat UI: the `@bg-bgi/magnus-ui` npm package
// (packages/sdk/ui inlines them at build time) and the shadcn registry
// (`magnus-chat` block — synced copies under packages/ui/registry/blocks/).
// Edit here; never fork.

export {
  MagnusChat,
  type MagnusChatComposerConfig,
  type MagnusChatEmptyContext,
  type MagnusChatProps,
  type MagnusChatSubmit,
} from "./chat";
export {
  CHICLET_LINK_STYLE,
  type ChicletProps,
  type ChicletRegistry,
  ChicletRegistryProvider,
  ChicletRenderer,
  DEFAULT_CHICLETS,
  useChicletRegistry,
} from "./chiclets";
export {
  type ActiveArtifactSuggestion,
  type ActiveSuggestion,
  ARTIFACT_REF_RE,
  type ArtifactMentionAttrs,
  type ArtifactMentionOptions,
  type ArtifactPickerItem,
  type ArtifactReferencePart,
  type AttachmentStatus,
  AttachmentsScroll,
  AttachmentTile,
  type AttachmentTileProps,
  type AttachmentValidation,
  AUTO_MODEL_SELECTION,
  attachmentAcceptsFile,
  attachmentCapacity,
  attachmentIcon,
  type ComposerAttachment,
  ComposerDictationButton,
  type ComposerDictationSource,
  type ComposerDictationState,
  type ComposerModelsSource,
  type ComposerProcessingUpdate,
  type ComposerUploadedFile,
  type ComposerUploadsSource,
  type EditorFilePart,
  type EditorNodeJSON,
  type EditorPart,
  type EditorTextPart,
  isSuggestionActive,
  type MentionCommand,
  type MentionManageLink,
  MessageAttachmentsScroll,
  ModelSelector,
  PASTE_AS_FILE_THRESHOLD,
  partsToPromptString,
  promptToTiptapDoc,
  RichComposer,
  type RichComposerHandle,
  type RichComposerProps,
  type RichComposerRightClusterContext,
  SentAttachmentTile,
  type SentAttachmentTileProps,
  SKILL_REF_RE,
  SkillChipHoverProvider,
  type SkillMentionAttrs,
  type SkillMentionOptions,
  type SkillPickerItem,
  type SkillReferencePart,
  serializeDocToParts,
  serializeEditorToParts,
  TipTapSkillInput,
  type TipTapSkillInputHandle,
  type TiptapDoc,
  toArtifactMentionAttrs,
  toAttachmentFilePart,
  toMentionAttrs,
  type UseAttachmentsResult,
  useArtifactMention,
  useAttachments,
  useSkillMention,
  validateAttachment,
} from "./composer";
export {
  type ApprovalChoice,
  type ApprovalCopy,
  ApprovalInput,
} from "./composer/prompts/approval-input";
export {
  type ActiveApproval,
  type ChatApprovalsSource,
  findPendingApproval,
} from "./composer/prompts/approval-prompt";
export {
  AttachFilesField,
  type AttachFilesHandle,
  type AttachFilesState,
} from "./composer/prompts/attach-files-field";
export { MultipleChoiceInput } from "./composer/prompts/multiple-choice-input";
export {
  type ActiveMultipleChoice,
  type ActiveMultipleChoicePrompt,
  getActiveMultipleChoice,
} from "./composer/prompts/multiple-choice-prompt";
export { useApprovalPrompt } from "./composer/prompts/use-approval-prompt";
export {
  copyToClipboard,
  markdownForCopy,
  markdownToClipboardHtml,
  stripChicletRefs,
} from "./copy";
// Extracted tool-view families (plan 030): registries only — the view
// components stay behind each registry's `lazy(() => import(...))` so the
// base block bundle never inlines them. Deep-path source consumers can
// also import `./tools/<family>` for the view components directly.
export { DEFAULT_TOOL_VIEWS } from "./default-tool-views";
export {
  type ChatFeedbackSource,
  FEEDBACK_COMMENT_MAX_LENGTH,
  FeedbackModal,
  type FeedbackModalProps,
  type FeedbackSubmitInput,
  type UseFeedbackSubmitResult,
  useFeedbackSubmit,
} from "./feedback";
export {
  type ChatHistorySource,
  ChatHistorySwitcher,
} from "./history-switcher";
export {
  type FetchToolOutput,
  LazyToolOutputProvider,
  type LazyToolOutputResolver,
  type LazyToolOutputResult,
  useLazyToolOutputResolver,
} from "./lazy-tool-output";
export {
  type CaptionsPlacement,
  ComposerLiveCallButton,
  type ComposerLiveCallPhase,
  type ComposerLiveCallSource,
  LiveCallPill,
  type LiveCallPillSource,
} from "./live-call";
export {
  isProcessingFailed,
  type MagnusChatMessagePart,
  type MagnusChatProcessingSnapshot,
  Message,
} from "./message";
export { MessageActions, type MessageFeedbackProps } from "./message-actions";
export { MessageErrorBoundary } from "./message-error-boundary";
export {
  getMessageCreatedAt,
  MessageTimestamp,
  renderUserMessageTimestamp,
} from "./message-timestamp";
export {
  MessageLinkProvider,
  type MessageLinkRenderer,
  MessageResponse,
  type MessageResponseProps,
  SmoothTextBlock,
} from "./response";
export { ChatHydrationSkeleton } from "./skeleton";
export { isToolPart, ToolPart, type ToolPartLike } from "./tool-part";
export { ToolViewsProvider, useExtraToolViews } from "./tool-views";
export type { ToolViewInvocation } from "./tools/_framework/invocation";
// The block's ToolTimeline provides this per-row; host-side tool views built
// on their OWN ToolItem copy (e.g. the SDK's) read it as a fallback so their
// rows register and paint connectors inside the block's groups.
export {
  TimelineContext,
  type TimelineContextValue,
} from "./tools/_framework/tool-item";
export { multipleChoiceToolRegistry } from "./tools/multiple-choice/registry";
export { weatherToolRegistry } from "./tools/weather/registry";
export { webSearchToolRegistry } from "./tools/web-search/registry";
export {
  AskedFromTag,
  askedFromHeaders,
  getMessageAskedFrom,
  type MagnusChatLinkComponent,
  type MessageAskedFrom,
  Transcript,
} from "./transcript";
export {
  classifyTransportError,
  TransportErrorBanner,
  useTransportErrorBanner,
} from "./transport-error-banner";
export { useElementHeight } from "./use-element-height";
export { useSmoothText } from "./use-smooth-text";
export { VoiceSessionChip, type VoiceSessionPartData } from "./voice-session";
